import { useCallback } from 'react';
import { TITLE_OWNER_DOCUMENT_VERIFICATION_TIMEOUT_MS } from '@brighte/validation';
import {
  GetTitleOwnerDocumentVerificationDocument,
  GetTitleOwnerDocumentVerificationQuery,
  GetTitleOwnerDocumentVerificationQueryVariables,
  RequestTitleOwnerDocumentVerificationMutationVariables,
  TitleOwnerVerificationStatus,
  VerifyTitleOwnerDocumentsMutation,
  useFetcher,
  useRequestTitleOwnerDocumentVerificationMutation,
} from '@graphql/index';

const DEFAULT_POLL_INTERVAL_MS = 2_000;

type VerificationPollingOptions = {
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
};

const wait = (durationMs: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, durationMs);
  });

export function useTitleOwnerDocumentVerification({
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  pollTimeoutMs = TITLE_OWNER_DOCUMENT_VERIFICATION_TIMEOUT_MS,
}: VerificationPollingOptions = {}) {
  const { mutateAsync: requestVerification } = useRequestTitleOwnerDocumentVerificationMutation({
    useErrorBoundary: false,
  });
  const getVerification = useFetcher<
    GetTitleOwnerDocumentVerificationQuery,
    GetTitleOwnerDocumentVerificationQueryVariables
  >(GetTitleOwnerDocumentVerificationDocument);

  const mutateAsync = useCallback(
    async (
      variables: RequestTitleOwnerDocumentVerificationMutationVariables,
    ): Promise<VerifyTitleOwnerDocumentsMutation> => {
      const requested = await requestVerification(variables);
      const { applicationId, titleSearchOwnerId } = variables.input;
      const { requestId } = requested.requestTitleOwnerDocumentVerification;
      const pollingStartedAt = Date.now();

      while (Date.now() - pollingStartedAt < pollTimeoutMs) {
        const verification = await getVerification({
          input: { applicationId, titleSearchOwnerId, requestId },
        });
        const { result, verificationStatus } = verification.getTitleOwnerDocumentVerification;

        if (result) {
          return { verifyTitleOwnerDocuments: result };
        }
        if (verificationStatus !== TitleOwnerVerificationStatus.PENDING) {
          throw new Error('Title-owner document verification failed.');
        }

        await wait(pollIntervalMs);
      }

      throw new Error('Title-owner document verification timed out.');
    },
    [getVerification, pollIntervalMs, pollTimeoutMs, requestVerification],
  );

  return { mutateAsync };
}
